import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { complianceServices } from '../data/services';
import ServiceCard from '../components/common/ServiceCard';

const Compliances = () => {
  const complianceBenefits = [
    'Timely filing to avoid penalties',
    'Expert CA and CS team',
    'Complete documentation support',
    'Regular compliance reminders',
    'Audit support included',
    'Affordable yearly packages',
  ];

  const penalties = [
    { title: 'Late ROC Filing', penalty: 'Rs. 100/day', description: 'Per form per day delay' },
    { title: 'DIN KYC Default', penalty: 'Rs. 5,000', description: 'Plus DIN deactivation' },
    { title: 'GST Return Delay', penalty: 'Rs. 50-200/day', description: 'Per return per day' },
    { title: 'ITR Late Filing', penalty: 'Rs. 5,000-10,000', description: 'Depending on income' },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="compliances-page">
      <Helmet>
        <title>Compliance Services - Income Tax, ROC Filing, DIN KYC | GK Legal Solutions</title>
        <meta name="description" content="Income Tax Filing, Company Compliance, LLP Compliance, OPC Compliance, DIN KYC services. Stay compliant and avoid penalties with expert CA team." />
        <link rel="canonical" href="https://gklegalsolutions.com/compliances" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Compliance Services</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 mb-6">
                Stay Compliant, Stay Worry-Free
              </h1>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
                From income tax filing to company compliances, we ensure your business meets all statutory requirements on time, every time. Avoid penalties with our expert compliance services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary flex items-center gap-2" data-testid="compliance-get-started-btn">
                    Get Compliance Package
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+919820669943">
                  <Button variant="outline" className="btn-secondary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Talk to Expert
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800"
                alt="Compliance documents"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-slate-900">100%</p>
                    <p className="font-body text-sm text-slate-600">Compliance Rate</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Banner */}
      <section className="py-8 bg-amber-50 border-y border-amber-200">
        <div className="container-custom">
          <div className="flex items-center gap-4 justify-center text-center">
            <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0" />
            <p className="font-body text-amber-800">
              <strong>Don't Risk Penalties!</strong> Late or non-compliance can result in heavy fines and legal action. Stay compliant with our expert services.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Compliance Services
            </h2>
            <p className="font-body text-lg text-slate-600">
              Comprehensive compliance solutions for all business types.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceServices.map((service) => (
              <ServiceCard key={service.id} service={service} linkTo="/contact" />
            ))}
          </div>
        </div>
      </section>

      {/* Penalty Info */}
      <section className="section-spacing bg-slate-900 text-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Know the Risks</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mt-3 mb-4">
              Non-Compliance Penalties
            </h2>
            <p className="font-body text-lg text-slate-400">
              Understanding the cost of missing compliance deadlines.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {penalties.map((item, index) => (
              <div key={index} className="bg-slate-800 p-6 rounded-xl hover:bg-slate-700 transition-colors">
                <AlertTriangle className="w-8 h-8 text-amber-500 mb-4" />
                <h3 className="font-heading text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="font-heading text-2xl font-bold text-amber-400 mb-2">{item.penalty}</p>
                <p className="font-body text-sm text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-8">
                Expert Compliance Management
              </h2>
              <div className="space-y-4">
                {complianceBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="font-body text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
              <Link to="/contact" className="inline-block mt-8">
                <Button className="btn-primary">Get Compliance Package</Button>
              </Link>
            </div>
            <div className="bg-white p-10 rounded-2xl shadow-lg">
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-6">Annual Compliance Calendar</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="font-body text-slate-700">DIN KYC</span>
                  <span className="font-body text-sm text-primary font-medium">By 30th September</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="font-body text-slate-700">Company Annual Return (MGT-7)</span>
                  <span className="font-body text-sm text-primary font-medium">Within 60 days of AGM</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="font-body text-slate-700">Financial Statements (AOC-4)</span>
                  <span className="font-body text-sm text-primary font-medium">Within 30 days of AGM</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-slate-200">
                  <span className="font-body text-slate-700">Income Tax Return</span>
                  <span className="font-body text-sm text-primary font-medium">By 31st July / October</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-body text-slate-700">GST Annual Return</span>
                  <span className="font-body text-sm text-primary font-medium">By 31st December</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Never Miss a Compliance Deadline
          </h2>
          <p className="font-body text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Let our experts handle all your compliance requirements. Stay focused on growing your business.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-primary hover:bg-slate-100 px-8 py-3 font-semibold">
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Compliances;
